import clsx from 'clsx'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components-latest/primitives/Collapsible'
import { ArrowDown } from '@/components-latest/primitives/Icons'
import Text from '@/components-latest/primitives/Text'
import Widget from '@/components-latest/primitives/Widget'

import Styles from './Accordion.module.scss'

interface IAccordionProps {
  children: React.ReactNode
  label: string
  icon?: React.ReactNode
  className?: string
  variant?: 'withButton' | 'simple'
}

const Accordion: React.FC<IAccordionProps> = ({
  label,
  icon,
  children,
  className,
  variant = 'withButton'
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const buttonLabel = isOpen ? t('common.cta.collapse') : t('common.cta.expand')

  const handleWidgetClick = () => setIsOpen(state => !state)

  return (
    <Collapsible open={isOpen} onOpenChange={open => setIsOpen(open)}>
      <div className={clsx(Styles.accordion, className)}>
        <div className={Styles.header}>
          <div>
            <Text variant="nextSubtitle" weight="bold" className={Styles.title}>
              {icon && <span className={Styles.iconWrapper}>{icon}</span>}
              {label}
            </Text>
          </div>
          <CollapsibleTrigger asChild>
            {variant === 'withButton' ? (
              <Widget
                onClick={handleWidgetClick}
                className={Styles.expandButton}>
                {buttonLabel}
                <ArrowDown
                  className={clsx(Styles.arrow, isOpen && Styles.rotate180)}
                />
              </Widget>
            ) : (
              <button>
                <ArrowDown
                  className={clsx(Styles.arrow, isOpen && Styles.rotate180)}
                />
              </button>
            )}
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className={Styles.content}>
          {children}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

export default Accordion
